import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { ROLE_CONFIG, type RoleConfig } from '@/config/role-config';
import { authService } from '@/services/auth.service';
import { router } from '@/router';

import type { AuthUser, LoginRequest } from '@/types/auth.types';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const user = ref<AuthUser | null>(null);
    const accessToken = ref<string | null>(null);
    const refreshToken = ref<string | null>(null);

    const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null);
    const userRole = computed(() => user.value?.role ?? null);
    const isAdmin = computed(() => userRole.value === 'admin');

    const roleConfig = computed<RoleConfig | null>(() => {
      const role = userRole.value;

      if (!role) {
        return null;
      }

      return ROLE_CONFIG[role];
    });

    const userId = computed(() => user.value?.id ?? null);
    const canWriteProjects = computed(() => roleConfig.value?.projects.canEdit ?? false);
    const canDeleteProjects = computed(() => roleConfig.value?.projects.canDelete ?? false);
    const canFinishProjects = computed(() => roleConfig.value?.projects.canFinish ?? false);
    const canCreateContractors = computed(() => roleConfig.value?.contractors.canCreate ?? false);
    const canEditContractors = computed(() => roleConfig.value?.contractors.canEdit ?? false);
    const canDeleteContractors = computed(() => roleConfig.value?.contractors.canDelete ?? false);
    const canOverwriteProgressReport = computed(
      () => roleConfig.value?.projects.canOverwriteProgressReport ?? false,
    );

    async function login(credentials: LoginRequest): Promise<void> {
      const response = await authService.login(credentials);

      accessToken.value = response.tokens.accessToken;
      refreshToken.value = response.tokens.refreshToken;
      user.value = response.user;
    }

    async function refresh(): Promise<void> {
      if (!refreshToken.value) {
        clearAuth();
        throw new Error('No refresh token');
      }

      try {
        const response = await authService.refresh(refreshToken.value);

        accessToken.value = response.tokens.accessToken;
        refreshToken.value = response.tokens.refreshToken;
        user.value = response.user;
      } catch {
        clearAuth();
        throw new Error('Token refresh failed');
      }
    }

    async function logout(): Promise<void> {
      try {
        await authService.logout();
      } catch {
        // Ignore errors during logout — clear local state regardless
      }

      clearAuth();
      await router.push('/login');
    }

    function clearAuth(): void {
      user.value = null;
      accessToken.value = null;
      refreshToken.value = null;
    }

    return {
      user,
      accessToken,
      refreshToken,
      isAuthenticated,
      userId,
      userRole,
      isAdmin,
      roleConfig,
      canWriteProjects,
      canDeleteProjects,
      canFinishProjects,
      canCreateContractors,
      canEditContractors,
      canDeleteContractors,
      canOverwriteProgressReport,
      login,
      refresh,
      logout,
    };
  },
  {
    persist: {
      paths: ['user', 'accessToken', 'refreshToken'],
    },
  },
);
